import { useEffect, useState } from "react";
import styled from "styled-components";

import Overlay from "../../Componentes/Overlay";
import Modal from "../../Componentes/Utils/Modal";
import Formulario from "../../Componentes/Utils/Formulario";
import FormComp from "../../Componentes/FormComp";

import { useModal } from "../../contexts/ModalContext";
import { useToast } from "../../contexts/ToastContext";
import useApi from "../../hooks/useApi";

const Title = styled.h3`
    margin: 0 0 4px 0;
    font-size: 14px;
    font-weight: 700;
    color: #1f2937;
`;

const Subtitle = styled.div`
    font-size: 12px;
    color: #6b7280;
    margin-bottom: 12px;
`;

const TableWrap = styled.div`
    max-height: 240px;
    overflow: auto;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    margin-bottom: 12px;

    table {
        width: 100%;
        border-collapse: collapse;
        font-size: 12px;
    }
    th, td {
        padding: 6px 8px;
        text-align: left;
        border-bottom: 1px solid #f1f5f9;
    }
    th {
        background: #f8fafc;
        position: sticky;
        top: 0;
    }
    .num {
        text-align: right;
        font-variant-numeric: tabular-nums;
    }
    .empty {
        padding: 14px;
        color: #6b7280;
        text-align: center;
    }
`;

const Row = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    gap: 8px;
    align-items: end;
`;

const ActionsCell = styled.div`
    display: flex;
    justify-content: flex-end;
`;

const EditBtn = styled.button`
    border: 1px solid #d1d5db;
    background: #f8fafc;
    color: #1f2937;
    border-radius: 4px;
    padding: 4px 8px;
    font-size: 11px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #e2e8f0;
        border-color: #94a3b8;
    }
`;

const CancelEditBtn = styled.button`
    margin-top: 8px;
    width: 100%;
    height: 40px;
    border-radius: 8px;
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #374151;
    font-size: 12px;
    font-weight: 700;
    cursor: pointer;

    &:hover {
        background: #f3f4f6;
    }
`;

const formatBR = (v) =>
    Number(v || 0).toLocaleString("pt-BR", {
        style: "currency",
        currency: "BRL",
    });

/**
 * Cadastro de faixas de km (centro_custo_faixa_folga) para a `obra`.
 * Overlay não fecha ao clicar — fecha só pelo X.
 */
const ModalCadastroFaixas = ({ obra }) => {

    const api = useApi();
    const { closeModal } = useModal();
    const { notifySuccess, notifyError } = useToast();

    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);

    const [kmMin, setKmMin] = useState("");
    const [kmMax, setKmMax] = useState("");
    const [valor, setValor] = useState("");
    const [gozo, setGozo] = useState("");
    const [editingId, setEditingId] = useState(null);

    const fetchList = async () => {
        if (!obra?.id) return;
        setLoading(true);
        try {
            const { data } = await api.get(
                `/Painel/API/FolgasCampo/Faixas/List/index.php?centro_custo_folga_id=${obra.id}`
            );
            setItems(data?.success && Array.isArray(data.items) ? data.items : []);
        } catch (e) {
            notifyError(e?.response?.data?.message || "Falha ao listar faixas.");
            setItems([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchList();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [obra?.id]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!obra?.id) return;

        const min = parseInt(kmMin, 10);
        const max = kmMax === "" ? null : parseInt(kmMax, 10);
        const val = parseFloat(String(valor).replace(",", "."));
        const gz = parseInt(gozo, 10);

        if (!Number.isFinite(min) || min < 0) {
            notifyError("Informe km_min (>= 0).");
            return;
        }
        if (max !== null && (!Number.isFinite(max) || max < min)) {
            notifyError("km_max deve ser >= km_min.");
            return;
        }
        if (!Number.isFinite(val) || val < 0) {
            notifyError("Informe um valor de passagem válido.");
            return;
        }
        if (!Number.isFinite(gz) || gz < 0) {
            notifyError("Informe os dias de gozo.");
            return;
        }

        setSubmitting(true);
        try {
            await api.post("/Painel/API/FolgasCampo/Faixas/Save/index.php", {
                id: editingId || undefined,
                centro_custo_folga_id: obra.id,
                km_min: min,
                km_max: max,
                valor_passagem: val,
                gozo_dias: gz,
            });
            notifySuccess(editingId ? "Faixa atualizada." : "Faixa cadastrada.");
            setKmMin("");
            setKmMax("");
            setValor("");
            setGozo("");
            setEditingId(null);
            await fetchList();
        } catch (err) {
            notifyError(err?.response?.data?.message || "Erro ao salvar faixa.");
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditRow = (row) => {
        setEditingId(row.id);
        setKmMin(String(row.km_min ?? ""));
        setKmMax(row.km_max == null ? "" : String(row.km_max));
        setValor(String(row.valor_passagem ?? ""));
        setGozo(String(row.gozo_dias ?? ""));
    };

    const handleCancelEdit = () => {
        setEditingId(null);
        setKmMin("");
        setKmMax("");
        setValor("");
        setGozo("");
    };

    if (!obra) return null;

    return (
        <Overlay state>
            <Modal size="lg" header close={closeModal}>
                <Title>VALORES POR FAIXA DE KM — {obra.obra}</Title>
                <Subtitle>Prazo do ciclo: <strong>{obra.dias_prazo}</strong> dias</Subtitle>

                <TableWrap>
                    {loading ? (
                        <div className="empty">Carregando…</div>
                    ) : items.length === 0 ? (
                        <div className="empty">Nenhuma faixa cadastrada.</div>
                    ) : (
                        <table>
                            <thead>
                                <tr>
                                    <th className="num">KM MIN</th>
                                    <th className="num">KM MAX</th>
                                    <th className="num">VALOR PASSAGEM</th>
                                    <th className="num">GOZO (DIAS)</th>
                                    <th className="num">AÇÕES</th>
                                </tr>
                            </thead>
                            <tbody>
                                {items.map((f) => (
                                    <tr key={f.id}>
                                        <td className="num">{f.km_min}</td>
                                        <td className="num">{f.km_max != null ? f.km_max : "∞"}</td>
                                        <td className="num">{formatBR(f.valor_passagem)}</td>
                                        <td className="num">{f.gozo_dias}</td>
                                        <td className="num">
                                            <ActionsCell>
                                                <EditBtn type="button" onClick={() => handleEditRow(f)}>
                                                    EDITAR
                                                </EditBtn>
                                            </ActionsCell>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </TableWrap>

                <Formulario onSubmit={handleSubmit}>
                    <Row>
                        <FormComp
                            title="KM MIN"
                            type="number"
                            valor={kmMin}
                            changeFunc={(e) => setKmMin(e.target.value)}
                            required
                        />
                        <FormComp
                            title="KM MAX (vazio = sem teto)"
                            type="number"
                            valor={kmMax}
                            changeFunc={(e) => setKmMax(e.target.value)}
                        />
                        <FormComp
                            title="VALOR PASSAGEM (R$)"
                            type="number"
                            valor={valor}
                            changeFunc={(e) => setValor(e.target.value)}
                            required
                        />
                        <FormComp
                            title="GOZO (DIAS)"
                            type="number"
                            valor={gozo}
                            changeFunc={(e) => setGozo(e.target.value)}
                            required
                        />
                    </Row>
                    <FormComp
                        type="submit"
                        valor={submitting ? "SALVANDO…" : (editingId ? "SALVAR EDIÇÃO" : "ADICIONAR FAIXA")}
                        disabled={submitting}
                    />
                    {editingId && (
                        <CancelEditBtn type="button" onClick={handleCancelEdit}>
                            CANCELAR EDIÇÃO
                        </CancelEditBtn>
                    )}
                </Formulario>
            </Modal>
        </Overlay>
    );
};

export default ModalCadastroFaixas;
